import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './Poll.css';

interface PollOption {
  id: number;
  option_text: string;
  vote_count: number;
}

interface Poll {
  id: number;
  title: string;
  description?: string;
  created_by_username: string;
  created_at: string;
  ends_at?: string;
  vote_count: number;
  options: PollOption[];
}

interface PollProps {
  poll: Poll;
  userVote?: number | null;
  onVoteUpdate?: () => void;
}

const PollComponent: React.FC<PollProps> = ({ poll, userVote: initialUserVote, onVoteUpdate }) => {
  const { user } = useAuth();
  const [userVote, setUserVote] = useState<number | null>(initialUserVote || null);
  const [loadingVote, setLoadingVote] = useState(false);

  useEffect(() => {
    // Lade User-Vote wenn nicht initial gesetzt
    if (initialUserVote === undefined && user) {
      const fetchUserVote = async () => {
        try {
          const response = await api.get(`/polls/${poll.id}`);
          setUserVote(response.data.userVote);
        } catch (error) {
          console.error('Fehler beim Laden des User-Votes:', error);
        }
      };
      fetchUserVote();
    }
  }, [poll.id, user, initialUserVote]);
  const [voting, setVoting] = useState(false);
  const [options, setOptions] = useState<PollOption[]>(poll.options);
  const [totalVotes, setTotalVotes] = useState(poll.vote_count);

  const isExpired = poll.ends_at ? new Date(poll.ends_at) < new Date() : false;
  const canVote = !isExpired && user !== null;

  const handleVote = async (optionId: number) => {
    if (!user || voting || loadingVote) return;
    if (userVote === optionId) {
      // Stimme zurückziehen
      try {
        setVoting(true);
        setLoadingVote(true);
        await api.delete(`/polls/${poll.id}/vote`);
        setUserVote(null);
        // Optionen neu laden
        const response = await api.get(`/polls/${poll.id}`);
        setOptions(response.data.poll.options);
        setTotalVotes(response.data.poll.vote_count);
        if (onVoteUpdate) onVoteUpdate();
      } catch (error) {
        console.error('Fehler beim Zurückziehen der Stimme:', error);
      } finally {
        setVoting(false);
        setLoadingVote(false);
      }
    } else {
      // Abstimmen
      try {
        setVoting(true);
        setLoadingVote(true);
        const response = await api.post(`/polls/${poll.id}/vote`, { option_id: optionId });
        setUserVote(optionId);
        setOptions(response.data.options);
        setTotalVotes(response.data.totalVotes);
        if (onVoteUpdate) onVoteUpdate();
      } catch (error: any) {
        console.error('Fehler beim Abstimmen:', error);
        alert(error.response?.data?.error || 'Fehler beim Abstimmen');
      } finally {
        setVoting(false);
        setLoadingVote(false);
      }
    }
  };

  const getPercentage = (voteCount: number): number => {
    if (totalVotes === 0) return 0;
    return Math.round((voteCount / totalVotes) * 100);
  };

  return (
    <div className="poll-card">
      <div className="poll-header">
        <h3 className="poll-title">{poll.title}</h3>
        <div className="poll-meta">
          <span className="poll-author">von {poll.created_by_username}</span>
          <span className="poll-date">
            {new Date(poll.created_at).toLocaleDateString('de-DE')}
          </span>
          {poll.ends_at && (
            <span className={`poll-ends ${isExpired ? 'expired' : ''}`}>
              {isExpired ? 'Abgelaufen' : `Endet: ${new Date(poll.ends_at).toLocaleDateString('de-DE')}`}
            </span>
          )}
        </div>
      </div>
      {poll.description && <p className="poll-description">{poll.description}</p>}
      <div className="poll-options">
        {options.map((option) => {
          const percentage = getPercentage(option.vote_count);
          const isSelected = userVote === option.id;
          const isDisabled = !canVote || voting || loadingVote;

          return (
            <button
              key={option.id}
              type="button"
              className={`poll-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
              onClick={() => handleVote(option.id)}
              disabled={isDisabled}
            >
              <div className="poll-option-content">
                <span className="poll-option-text">{option.option_text}</span>
                {totalVotes > 0 && (
                  <span className="poll-option-stats">
                    {option.vote_count} Stimmen ({percentage}%)
                  </span>
                )}
              </div>
              {totalVotes > 0 && (
                <div className="poll-option-bar">
                  <div
                    className="poll-option-fill"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              )}
            </button>
          );
        })}
      </div>
      <div className="poll-footer">
        <span className="poll-total-votes">{totalVotes} {totalVotes === 1 ? 'Stimme' : 'Stimmen'}</span>
        {userVote && <span className="poll-user-vote">✓ Du hast abgestimmt</span>}
      </div>
    </div>
  );
};

export default PollComponent;

